import { z } from "zod";
import { parseE164 } from "../phone";

// --- Platform roles ---
const platformRoleValues = [
  "SUPERADMIN",
  "MANAGER",
  "STAFF",
  "TRAINER",
] as const;
export const platformRoleSchema = z.enum(platformRoleValues);
export type PlatformRole = z.infer<typeof platformRoleSchema>;
export const PlatformRoleValues = {
  SUPERADMIN: "SUPERADMIN",
  MANAGER: "MANAGER",
  STAFF: "STAFF",
  TRAINER: "TRAINER",
} as const satisfies Record<PlatformRole, PlatformRole>;

/**
 * Los roles que un SUPERADMIN puede asignar desde el panel. TRAINER queda
 * afuera: su cuenta nace del registro publico y no tiene gimnasio, mientras que
 * el formulario de usuarios exige elegir uno.
 */
export type AssignablePlatformRole = Exclude<PlatformRole, "TRAINER">;

export const isAssignablePlatformRole = (
  role: PlatformRole,
): role is AssignablePlatformRole => role !== PlatformRoleValues.TRAINER;

// --- Plan interval ---
const planIntervalValues = [
  "DAY",
  "WEEK",
  "MONTH",
  "QUARTER",
  "SEMESTER",
  "YEAR",
  "CUSTOM",
] as const;
export const planIntervalSchema = z.enum(planIntervalValues);
export type PlanInterval = z.infer<typeof planIntervalSchema>;
export const PLAN_INTERVALS = planIntervalValues;

// --- Gender ---
const genderValues = ["MALE", "FEMALE", "OTHER", "PREFER_NOT_TO_SAY"] as const;
export const genderSchema = z.enum(genderValues);
export type Gender = z.infer<typeof genderSchema>;

// --- Other income category ---
const otherIncomeCategoryValues = [
  "office",
  "services",
  "events",
  "sponsorships",
  "other",
] as const;
export const otherIncomeCategorySchema = z.enum(otherIncomeCategoryValues);
export type OtherIncomeCategory = z.infer<typeof otherIncomeCategorySchema>;
export const OTHER_INCOME_CATEGORIES = otherIncomeCategoryValues;

// --- Activity log action ---
const activityLogActionValues = ["INSERT", "UPDATE", "DELETE"] as const;
export const activityLogActionSchema = z.enum(activityLogActionValues);
export type ActivityLogAction = z.infer<typeof activityLogActionSchema>;

// --- Reusable primitives ---
export const publicIdSchema = z.string().min(1);
export const optionalPublicIdSchema = z.string().min(1).optional();
export const isoDateStringSchema = z.string(); // ISO date/datetime from API
export const optionalIsoDateSchema = z.string().nullish();

// Acepta "+" opcional y separadores comunes; solo exige 7–15 dígitos.
// Un valor que PhoneInputField muestra vacío (sin dígitos tras el código de
// país: "+56", "-", " ") cuenta como vacío y se normaliza a "".
const isBlankPhone = (v: string) => parseE164(v).digits === "";

export const permissivePhoneSchema = (message: string) =>
  z
    .string()
    .refine((v) => {
      if (isBlankPhone(v)) return true;
      if (!/^[+\d\s().-]+$/.test(v)) return false;
      const digitCount = v.replace(/\D/g, "").length;
      return digitCount >= 7 && digitCount <= 15;
    }, message)
    .nullish()
    .or(z.literal(""))
    .transform((v) => (typeof v === "string" && isBlankPhone(v) ? "" : v));

/** Schema for paginated list params (limit, offset) */
export const paginationParamsSchema = z.object({
  limit: z.number().optional(),
  offset: z.number().optional(),
});
